/**
 * 奖励分发方式 — GET/PUT /api/admin/reward-distributions
 */
import { useMemo, useState, useEffect } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
  Space,
  Tooltip,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { formatRewardDistributionValue } from '../../utils/reward-distribution-display';

interface DistRow {
  id: string;
  category: string;
  category_label: string;
  title_zh: string;
  description_zh: string;
  trigger_zh: string;
  theory_ref: string | null;
  config_key: string | null;
  value_kind: string;
  default_value: string;
  effective_value: string;
  source: 'uo_config' | 'code_default';
  editable: boolean;
  ledger_actions: string[];
}

interface RpQuotaRow {
  level_code: string;
  quota_default: string;
  effective_quota: string;
  source: 'uo_config' | 'code_default';
}

interface ListPayload {
  items: DistRow[];
  rp_sp3_quotas: RpQuotaRow[];
}

const STAR_FIXED_IDS = new Set(['static_main_principal', 'static_main_exit_multiplier']);
const MAP_EDITOR_IDS = new Set([
  ...STAR_FIXED_IDS,
  'static_new_system_register_exit',
  'static_legacy_register_exit',
]);

const CATEGORY_ORDER = [
  'static',
  'dynamic',
  'withdraw_split',
  'activation',
  'quota',
  'feature',
];

export function RewardDistributionPage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canEdit = hasNode('system.config.edit');

  const { data, isFetching } = useQuery({
    queryKey: ['admin', 'reward-distributions'],
    queryFn: () => api.get<ListPayload>('/api/admin/reward-distributions'),
  });

  const [editRow, setEditRow] = useState<DistRow | null>(null);
  const [editQuota, setEditQuota] = useState<RpQuotaRow | null>(null);
  const [form] = Form.useForm();
  const [quotaForm] = Form.useForm();
  const editValueDraft = Form.useWatch('value', form);

  const items = data?.items ?? [];
  const rpQuotas = data?.rp_sp3_quotas ?? [];

  const byCategory = useMemo(() => {
    const m = new Map<string, DistRow[]>();
    for (const c of CATEGORY_ORDER) m.set(c, []);
    for (const row of items) {
      const list = m.get(row.category) ?? [];
      list.push(row);
      m.set(row.category, list);
    }
    return m;
  }, [items]);

  async function saveDist(values: { value: string; note?: string }) {
    if (!editRow) return;
    try {
      await api.put(`/api/admin/reward-distributions/${encodeURIComponent(editRow.id)}`, values);
      message.success('已保存');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'reward-distributions'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '保存失败');
    }
  }

  async function saveQuota(values: { quota: string }) {
    if (!editQuota) return;
    try {
      await api.put(
        `/api/admin/reward-distributions/rp-sp3-quota/${encodeURIComponent(editQuota.level_code)}`,
        values,
      );
      message.success('已保存');
      setEditQuota(null);
      qc.invalidateQueries({ queryKey: ['admin', 'reward-distributions'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '保存失败');
    }
  }

  async function seedDefaults() {
    try {
      const res = await api.post<{ upserted: number }>(
        '/api/admin/reward-distributions/seed-defaults',
        {},
      );
      message.success(`已写入 ${res.upserted} 条默认配置`);
      qc.invalidateQueries({ queryKey: ['admin', 'reward-distributions'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '失败');
    }
  }

  const columns = [
    {
      title: '名称',
      dataIndex: 'title_zh',
      width: 160,
      render: (t: string, row: DistRow) => (
        <div>
          <div>{t}</div>
          {row.theory_ref && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {row.theory_ref}
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      title: '触发时机',
      dataIndex: 'trigger_zh',
      ellipsis: true,
      width: 200,
    },
    {
      title: '说明',
      dataIndex: 'description_zh',
      ellipsis: true,
    },
    {
      title: '配置键',
      dataIndex: 'config_key',
      width: 200,
      render: (k: string | null) => (k ? <code style={{ fontSize: 11 }}>{k}</code> : '—'),
    },
    {
      title: '当前值（可读）',
      dataIndex: 'effective_value',
      width: 320,
      render: (v: string, row: DistRow) => {
        const { short, detail } = formatRewardDistributionValue(row.id, row.value_kind, v);
        const body = (
          <div style={{ lineHeight: 1.45 }}>
            <div style={{ fontWeight: 500 }}>{short}</div>
            {row.value_kind === 'percent' && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                比例 {v}
              </Typography.Text>
            )}
          </div>
        );
        return detail && detail !== short ? (
          <Tooltip
            title={
              <pre style={{ margin: 0, maxHeight: 280, overflow: 'auto', fontSize: 11 }}>
                {detail}
              </pre>
            }
          >
            {body}
          </Tooltip>
        ) : (
          body
        );
      },
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: 90,
      render: (s: string) =>
        s === 'uo_config' ? <Tag color="green">uo_config</Tag> : <Tag>代码默认</Tag>,
    },
    {
      title: '操作',
      key: 'op',
      width: 80,
      render: (_: unknown, row: DistRow) =>
        row.editable && canEdit && row.id !== 'rp_sp3_quota' ? (
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditRow(row);
              form.setFieldsValue({
                value:
                  row.value_kind === 'json'
                    ? row.effective_value
                    : row.effective_value,
                note: row.title_zh,
              });
            }}
          >
            编辑
          </Button>
        ) : (
          <span className="text-gray-400">只读</span>
        ),
    },
  ];

  const tabItems = CATEGORY_ORDER.filter((c) => (byCategory.get(c)?.length ?? 0) > 0).map(
    (c) => ({
      key: c,
      label: byCategory.get(c)?.[0]?.category_label ?? c,
      children: (
        <Table<DistRow>
          size="small"
          bordered
          rowKey="id"
          loading={isFetching}
          dataSource={byCategory.get(c) ?? []}
          columns={columns}
          pagination={false}
          scroll={{ x: 1280 }}
        />
      ),
    }),
  );

  return (
    <div>
      <Typography.Title level={4}>奖励分发配置</Typography.Title>
      <Typography.Paragraph type="secondary">
        列出已实现的动静态奖励分发方式。可编辑项写入 <code>uo_config</code>，保存后立即影响新发生的分发（已快照用户的出局倍数除外）。
        出局基数/倍数请用「仪表盘 → 出局上限表」点格子改。
      </Typography.Paragraph>
      {canEdit && (
        <Space style={{ marginBottom: 12 }}>
          <Button onClick={seedDefaults}>写入缺失默认键</Button>
        </Space>
      )}

      <Tabs items={tabItems} />

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        RP→SP3 等级配额
      </Typography.Title>
      <Table<RpQuotaRow>
        size="small"
        bordered
        rowKey="level_code"
        loading={isFetching}
        dataSource={rpQuotas}
        pagination={false}
        columns={[
          { title: '等级', dataIndex: 'level_code', width: 80 },
          { title: '代码默认', dataIndex: 'quota_default', width: 120 },
          { title: '生效配额', dataIndex: 'effective_quota', width: 120 },
          {
            title: '来源',
            dataIndex: 'source',
            width: 90,
            render: (s: string) =>
              s === 'uo_config' ? <Tag color="green">uo_config</Tag> : <Tag>代码默认</Tag>,
          },
          {
            title: '操作',
            key: 'op',
            width: 80,
            render: (_: unknown, row: RpQuotaRow) =>
              canEdit ? (
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setEditQuota(row);
                    quotaForm.setFieldsValue({ quota: row.effective_quota });
                  }}
                >
                  编辑
                </Button>
              ) : null,
          },
        ]}
      />

      <Modal
        open={editRow !== null}
        title={`编辑 — ${editRow?.title_zh ?? ''}`}
        width={720}
        onCancel={() => setEditRow(null)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={saveDist}>
          {editRow && (
            <Typography.Paragraph style={{ marginBottom: 12, padding: '8px 12px', background: '#fafafa', borderRadius: 6 }}>
              <Typography.Text type="secondary">可读预览：</Typography.Text>{' '}
              <strong>
                {
                  formatRewardDistributionValue(
                    editRow.id,
                    editRow.value_kind,
                    typeof editValueDraft === 'string' ? editValueDraft : editRow.effective_value,
                  ).short
                }
              </strong>
            </Typography.Paragraph>
          )}
          <Form.Item
            name="value"
            label={`值 (${editRow?.value_kind})`}
            rules={[{ required: true }]}
            extra={
              editRow && STAR_FIXED_IDS.has(editRow.id)
                ? '只改 E1 / E2 / E3，不能加档位。保存后只影响之后新快照。'
                : editRow && MAP_EDITOR_IDS.has(editRow.id)
                  ? '可改倍数。点「添加」可加币种键。保存后只影响之后新注册/开子的快照。'
                  : editRow?.value_kind === 'json'
                    ? '须为合法 JSON；比例类数组 pct 之和须为 1'
                    : undefined
            }
          >
            {editRow && MAP_EDITOR_IDS.has(editRow.id) ? (
              <KeyValueMapEditor
                fixedKeys={STAR_FIXED_IDS.has(editRow.id)}
                keyPlaceholder={
                  STAR_FIXED_IDS.has(editRow.id) ? 'E1 / E2 / E3' : 'sp1 / rp / sp3 / 自定义'
                }
              />
            ) : (
              <Input.TextArea rows={editRow?.value_kind === 'json' ? 12 : 3} />
            )}
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            代码默认：{editRow?.default_value?.slice(0, 200)}
            {editRow && editRow.default_value.length > 200 ? '…' : ''}
          </Typography.Text>
        </Form>
      </Modal>

      <Modal
        open={editQuota !== null}
        title={`RP→SP3 配额 — ${editQuota?.level_code ?? ''}`}
        onCancel={() => setEditQuota(null)}
        onOk={() => quotaForm.submit()}
        destroyOnClose
      >
        <Form form={quotaForm} layout="vertical" onFinish={saveQuota}>
          <Form.Item name="quota" label="配额 (RP)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function parseMapRows(raw: string | undefined): Array<{ k: string; v: string }> | null {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return Object.entries(parsed as Record<string, unknown>).map(([k, v]) => ({
      k,
      v: String(v ?? ''),
    }));
  } catch {
    return null;
  }
}

function stringifyMapRows(rows: Array<{ k: string; v: string }>): string {
  const obj: Record<string, string> = {};
  for (const row of rows) {
    const key = row.k.trim();
    if (!key) continue;
    obj[key] = row.v.trim();
  }
  return JSON.stringify(obj, null, 2);
}

function KeyValueMapEditor({
  value,
  onChange,
  keyPlaceholder,
  fixedKeys = false,
}: {
  value?: string;
  onChange?: (v: string) => void;
  keyPlaceholder: string;
  fixedKeys?: boolean;
}) {
  const parsed = parseMapRows(value);
  const [rows, setRows] = useState<Array<{ k: string; v: string }>>(() => parsed ?? []);

  useEffect(() => {
    const next = parseMapRows(value);
    if (next) setRows(fixedKeys ? next.filter((r) => /^(E1|E2|E3)$/i.test(r.k.trim())) : next);
  }, [value, fixedKeys]);

  if (parsed === null) {
    return <Input.TextArea rows={12} value={value} onChange={(e) => onChange?.(e.target.value)} />;
  }

  function commit(next: Array<{ k: string; v: string }>, persistEmpty = false) {
    setRows(next);
    if (persistEmpty) return;
    onChange?.(stringifyMapRows(next));
  }

  return (
    <div>
      {rows.map((row, i) => (
        <Space key={i} style={{ display: 'flex', marginBottom: 8 }} align="start">
          <Input
            placeholder={keyPlaceholder}
            value={row.k}
            disabled={fixedKeys}
            style={{ width: 160 }}
            onChange={(e) => {
              if (fixedKeys) return;
              const next = rows.map((r, j) => (j === i ? { ...r, k: e.target.value } : r));
              commit(next);
            }}
          />
          <Input
            placeholder="倍数或金额"
            value={row.v}
            style={{ width: 120 }}
            onChange={(e) => {
              const next = rows.map((r, j) => (j === i ? { ...r, v: e.target.value } : r));
              commit(next);
            }}
          />
          {!fixedKeys && (
            <Button
              type="text"
              danger
              icon={<MinusCircleOutlined />}
              onClick={() => commit(rows.filter((_, j) => j !== i))}
            />
          )}
        </Space>
      ))}
      {!fixedKeys && (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => commit([...rows, { k: '', v: '' }], true)}
        >
          添加
        </Button>
      )}
    </div>
  );
}
